import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, Table } from 'reactstrap';
import { getReportingProxyHeaders } from '../../../../utils/reportingClient';
import { getFunctionUrl } from '../../../../utils/apiHost';
import { useReportClock } from '../../reportClock';

const tones = { healthy: 'success', warning: 'warning', critical: 'danger', unknown: 'secondary' };
const labels = { healthy: 'Conforme au contrôle', warning: 'À vérifier', critical: 'Échec', unknown: 'Non vérifié' };

export default function DailyOperationalAudit() {
  const now = useReportClock();
  const [report, setReport] = useState(null);
  const [date, setDate] = useState('');
  const [error, setError] = useState('');
  const [runError, setRunError] = useState('');
  const [running, setRunning] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [cronConfigured, setCronConfigured] = useState(null);
  useEffect(() => {
    let active = true;
    const read = async () => {
      try {
        const response = await fetch(`${getFunctionUrl('/api/daily-audit')}${date ? `?date=${date}` : ''}`, { headers: await getReportingProxyHeaders(), cache: 'no-store' });
        const body = await response.json();
        if (!response.ok) throw new Error(body.message || 'Audit indisponible.');
        if (active) { setReport(body.report); setCronConfigured(body.cronConfigured); setError(''); setLoaded(true); }
      } catch (reason) { if (active) setError(reason.message); }
    };
    if (!running) read();
    return () => { active = false; };
  }, [now, date, running]);
  const run = async () => {
    setRunning(true); setError(''); setRunError('');
    try {
      const response = await fetch(getFunctionUrl('/api/daily-audit'), { method: 'POST', headers: await getReportingProxyHeaders() });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Exécution impossible.');
      setReport(body.report); setCronConfigured(body.cronConfigured); setDate(''); setLoaded(true);
    } catch (reason) { setRunError(reason.message); }
    finally { setRunning(false); }
  };
  const stale = report && now.getTime() - Date.parse(report.completedAt) > 30 * 3600000;
  return <Card><CardBody>
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
      <h2>Audit automatique quotidien{report ? ` — ${report.date}` : ''}</h2>
      <Button color="primary" onClick={run} disabled={running}>{running ? 'Audit en cours…' : 'Exécuter l’audit maintenant'}</Button>
    </div>
    <p>Chaque jour à 10 h UTC (6 h l’été / 5 h l’hiver à Toronto), même lorsque cette page est fermée. Historique conservé par date.</p>
    <label className="mb-3">Consulter une date <input className="form-control" type="date" value={date} onChange={event => { setDate(event.target.value); setReport(null); setLoaded(false); }} /></label>
    {date && <Button className="ms-2" color="secondary" onClick={() => { setDate(''); setReport(null); setLoaded(false); }}>Dernier audit</Button>}
    {error && <Alert color="danger">{error}</Alert>}
    {runError && <Alert color="danger">{runError}</Alert>}
    {cronConfigured === false && <Alert color="danger">Planification non opérationnelle : le secret de la tâche quotidienne doit être configuré.</Alert>}
    {stale && !date && <Alert color="warning">Le dernier audit a plus de 30 heures. La prochaine exécution doit être vérifiée ; ce résultat n’est pas un état actuel.</Alert>}
    {!report && !error && <p>{loaded ? 'Aucun audit enregistré pour cette date. Vous pouvez lancer un audit de la situation actuelle.' : 'Chargement du dernier audit…'}</p>}
    {report && <>
      <p>Terminé le {new Date(report.completedAt).toLocaleString('fr-CA', { timeZone: 'America/Toronto' })} · <Badge color={tones[report.status]}>{labels[report.status]}</Badge></p>
      <p>{report.counts.healthy} contrôles conformes · {report.counts.warning} à vérifier · {report.counts.critical} en échec · {report.counts.unknown} non vérifiés</p>
      <Table responsive className="table-dark"><thead><tr><th>Contrôle</th><th>Résultat</th><th>Constat vérifié</th></tr></thead><tbody>{report.checks.map(check => <tr key={check.id}><th scope="row">{check.label}</th><td><Badge color={tones[check.status]}>{labels[check.status]}</Badge></td><td>{check.detail}</td></tr>)}</tbody></Table>
      <h5>Périmètre et limites</h5><ul>{report.limitations.map(item => <li key={item}>{item}</li>)}</ul>
      <p>Ces résultats décrivent les contrôles automatiques effectués, pas une certification complète de sécurité.</p>
    </>}
  </CardBody></Card>;
}
